import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { yellow } from "@mui/material/colors";
import BasicMap from "components/BasicMap";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { FacilityAlternateIdKnownKind } from "encamp-shared/src/facility/alternateId";
import { findAlternateIds } from "encamp-shared/src/facilityAlternateId/findAlternateId";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { compact } from "lodash";
import { useParams } from "react-router-dom";
import { useWasteFacility } from "./useWasteFacility";

export const FacilityDetailsTile = () => {
  return (
    <DashboardTile size={{ sm: 12, md: 7 }}>
      <FacilityDetailsTileContent />
    </DashboardTile>
  );
};

const FacilityDetailsTileContent = () => {
  const theme = useTheme();
  const { facilityId } = useParams<{ facilityId: string }>();

  const { data } = useWasteFacility(facilityId);
  const {
    latitude,
    longitude,
    name,
    facilityAlternateIds,
    streetAddress1,
    streetAddress2,
    city,
    state,
    zip,
    county,
  } = data.facility || {};

  const epaIds = findAlternateIds(facilityAlternateIds ?? [], [
    FacilityAlternateIdKnownKind.EPA,
  ]).map((x) => x.value);

  const hasLatAndLong =
    typeof latitude === "number" && typeof longitude === "number";

  useBreadcrumb({ label: name ?? "" });

  return (
    <>
      <DashboardTile.Header
        title="Facility Details"
        infoTooltip="See basic information about your facility. If any of this information is incorrect or needs to be updated, please reach out to Encamp."
      />
      <DashboardTile.ContentArea>
        <Grid container spacing={5}>
          {hasLatAndLong && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack spacing={2}>
                <Box height={"262px"}>
                  <BasicMap latitude={latitude} longitude={longitude} />
                </Box>
                <Typography variant="caption">
                  {[latitude, longitude].join(", ")}
                </Typography>
              </Stack>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: hasLatAndLong ? 6 : 12 }}>
            <Stack spacing={2}>
              {epaIds.length > 0 && (
                <Stack alignItems="flex-start" spacing={1} gap={1}>
                  {epaIds.length > 1 && (
                    <Alert severity="warning" icon={<WarningAmber />}>
                      There are multiple EPA IDs associated with this facility
                    </Alert>
                  )}
                  <Stack spacing={1}>
                    {epaIds.map((epaId) => (
                      <Stack
                        spacing={2}
                        key={epaId}
                        alignItems="center"
                        flexWrap="wrap"
                        direction="row"
                      >
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          component="a"
                          href={`https://rcrapublic.epa.gov/rcra-hwip/search/results/site/${epaId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {epaId}
                          <Tooltip title="View RCRA Hazardous Waste Information Portal in new tab">
                            <IconButton>
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        {data.facility?.epaIdStatuses.find(
                          (e) => e.epaId === epaId
                        )?.isInactive && (
                          <Chip
                            label="EPA ID is inactive"
                            sx={{
                              backgroundColor: yellow[400],
                              color: theme.palette.text.primary,
                            }}
                          />
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              )}

              <Typography variant="h4">{name}</Typography>
              <Typography variant="body1">
                {compact([
                  compact([streetAddress1, streetAddress2, city]).join(" "),
                  compact([state, zip]).join(" "),
                ]).join(", ")}
              </Typography>
              {county && (
                <Typography variant="body1">
                  {county.endsWith(" County") ? county : `${county} County`}
                </Typography>
              )}
            </Stack>
          </Grid>
        </Grid>
      </DashboardTile.ContentArea>
    </>
  );
};
